import { Button } from "@/shared/components/ui/button";
import { DocumentState } from "@/services/backend/documents/types/state";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import {
  ConfirmationDocument,
  useHtzConfirmationPdfDownloadMutation,
  useLazyHtzConfirmationPdfPreviewQuery,
  useHtzConfirmationUpdateStateMutation,
} from "@/services/backend/htz/confirmations/api/confirmation-api";
import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";

export function ConfirmationPdf({
  confirmation,
}: {
  confirmation: Confirmation;
}) {
  const previewRequest = {
    confirmationId: confirmation.id,
    document: ConfirmationDocument.ALL,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyHtzConfirmationPdfPreviewQuery();

  const { write } = useErrValidationFailedContext();

  const { user } = useAuth();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Auftragsbestätugung")}
              documentNumber={
                <ReferenceNumber
                  referenceNumber={confirmation.referenceNumber}
                />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(confirmation) && (
                <>
                  <DocumentNotReadyAlert document={t("Auftragsbestätugung")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Um eine Dokumentennummer zu generieren müssen Sie die Auftragsbestätigung festschreiben. Sobald sie festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt. Sie können die Auftragsbestätigung jederzeit herunterladen. Das Wasserzeichen wird erst entfernt, wenn es festgeschrieben wurde.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: confirmation.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useHtzConfirmationUpdateStateMutation}
                    description={t(
                      "Die Auftragsbestätigung wird festgeschrieben. Danach kann die Auftragsbestätigung nicht mehr geändert werden.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={write}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmation.id}
                  document={ConfirmationDocument.CONFIRMATION}
                  text={t("Auftragsbestätugung")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  confirmationId,
  document: documentType,
  text,
}: {
  confirmationId: string;
  document: ConfirmationDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useHtzConfirmationPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const { user } = useAuth();

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      confirmationId,
      document: documentType,
      download: true,
      downloadBy: user?.id ?? "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Auftragsbestätugung herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Auftragsbestätugung heruntergeladen wird, wird es festgeschrieben. Es kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
